/**
 * Server-side access to the Emma agenda API.
 *
 * Every request to Emma goes through these helpers so the bearer token rides along.
 * The browser never talks to Emma directly; it goes through /api/emma/[...path].
 * Moving to another API version (e.g. /api/v2) is an EMMA_API_URL change.
 *
 * Paths start with "/" and are relative to EMMA_API_URL, e.g. "/conferences/226/events?depth=1".
 *
 * Error envelopes: Emma v2 answers errors (bad token, missing record) with HTTP 200 and a body
 * like {"success":false,"message":"...","error_code":401}. emmaFetch turns those into a Response
 * whose status is the error_code, so callers' `response.ok` checks see the failure.
 *
 * Last-good fallback: successful bodies are remembered per path. When Emma fails in a way that
 * is not about the record itself (bad token, 5xx, rate limit, network error), emmaFetch answers
 * with the last good body instead (header `x-emma-stale: <age seconds>`), so pages keep rendering
 * slightly old data rather than erroring. Callers with their own cache pass `lastGood: false`.
 *
 * After a cache bust (services/cache/registry.js) the first request for each path adds
 * fresh=true, which makes Emma build the response instead of serving its cached copy. It does
 * not clear Emma's cache; see docs/cache-busting.md.
 */
import { registerCache, lastBustAt } from "services/cache/registry"

const LAST_GOOD_MAX_ENTRIES = 300
const LAST_GOOD_MAX_BYTES = 1024 * 1024 // per entry; the full program feed has its own snapshot cache
const ENVELOPE_MAX_BYTES = 16 * 1024 // error envelopes are tiny
const ERROR_PREFIX = /^\s*\{\s*"success"\s*:\s*false/
// Emma keeps responses for 15 minutes; within this window after a bust, a path's first request is fresh.
const FRESH_AFTER_BUST_MS = 15 * 60 * 1000
const LOG_INTERVAL_MS = 60 * 1000

// Module state lives on globalThis so it survives dev-server HMR re-evaluation.
const state = (globalThis.__emmaServer ??= {
    lastGood: new Map(), // path -> { body, contentType, at }
    lastLogged: new Map(), // log key -> timestamp
    freshBustAt: 0, // the bust freshPaths belongs to
    freshPaths: new Set(), // paths already fetched fresh since that bust
})

registerCache("emma-last-good", () => state.lastGood.clear())

export function emmaUrl(path) {
    return (process.env.EMMA_API_URL || "").replace(/\/$/, "") + path
}

export function emmaAuthHeaders() {
    const token = process.env.EMMA_API_TOKEN
    return token ? { Authorization: `Bearer ${token}` } : {}
}

// fetch() against Emma; `init` is passed through (next.revalidate, cache, signal, ...).
// `init.lastGood: false` opts out of the last-good fallback.
export async function emmaFetch(path, { lastGood = true, ...init } = {}) {
    let response
    try {
        response = await fetch(emmaUrl(withFreshAfterBust(path)), {
            ...init,
            headers: { Accept: "application/json", ...init.headers, ...emmaAuthHeaders() },
        })
        response = await unwrapErrorEnvelope(response)
    } catch (err) {
        reportFailure(path, null, err.message)
        const stale = lastGood && staleResponse(path)
        if (stale) return stale
        throw err
    }

    if (response.ok) {
        if (lastGood) remember(path, response.clone()) // in the background; never delays the caller
        return response
    }

    reportFailure(path, response.status, response.headers.get("x-emma-error"))
    if (lastGood && isFallbackStatus(response.status)) {
        const stale = staleResponse(path)
        if (stale) return stale
    }
    return response
}

// GET returning { data, status, headers } and throwing on failure (err.response = { status, data }),
// the subset of axios' shape callers rely on.
export async function emmaGet(path, { headers, lastGood } = {}) {
    const response = await emmaFetch(path, { headers, lastGood, cache: "no-store" })
    const data = await response.json().catch(() => null)
    if (!response.ok) {
        const err = new Error(`Emma responded ${response.status} for ${path}`)
        err.response = { status: response.status, data }
        throw err
    }
    return { data, status: response.status, headers: response.headers }
}

/**
 * Boot-time check (called from instrumentation.js): one authenticated request so a missing or
 * rejected EMMA_API_TOKEN shows up in the logs at startup, not as a broken page later.
 */
export async function checkEmmaAccess(meetingId) {
    if (!process.env.EMMA_API_URL) {
        console.error("[emma] EMMA_API_URL is not set; Emma requests will fail")
        return false
    }
    if (!process.env.EMMA_API_TOKEN) {
        console.error("[emma] EMMA_API_TOKEN is not set; Emma will reject requests. Add it to .env.local (or the DO App env).")
    }
    try {
        const response = await emmaFetch(`/conferences/${meetingId}`, {
            cache: "no-store",
            lastGood: false,
            signal: AbortSignal.timeout(15 * 1000),
        })
        if (response.ok) {
            console.log(`[emma] access check ok (${process.env.EMMA_API_URL})`)
            return true
        }
        // reportFailure has already logged the specifics.
        return false
    } catch {
        return false
    }
}

// Emma v2 errors arrive as HTTP 200 with {"success":false,...}. Only the first bytes of the body
// are inspected (on a clone), so large feeds are never buffered or parsed twice.
async function unwrapErrorEnvelope(response) {
    if (!response.ok || !(response.headers.get("content-type") || "").includes("json")) return response

    const text = await readErrorEnvelope(response.clone())
    if (text === null) return response
    response.body?.cancel().catch(() => {})

    let envelope = null
    try {
        envelope = JSON.parse(text)
    } catch {}
    if (envelope?.success !== false) return new Response(text, { status: response.status, headers: decodedHeaders(response) })

    const code = Number(envelope.error_code)
    const status = code >= 400 && code <= 599 ? code : 502
    const message = headerSafe(envelope.message || "Emma error")
    const headers = decodedHeaders(response)
    headers.set("x-emma-error", message)
    return new Response(text, { status, statusText: message, headers })
}

// The whole body if it starts like an error envelope and is envelope-sized, otherwise null.
async function readErrorEnvelope(clone) {
    const reader = clone.body?.getReader()
    if (!reader) return null
    const decoder = new TextDecoder()
    let text = ""
    try {
        for (;;) {
            const { done, value } = await reader.read()
            if (done) {
                text += decoder.decode()
                return ERROR_PREFIX.test(text) ? text : null
            }
            text += decoder.decode(value, { stream: true })
            if (text.length > ENVELOPE_MAX_BYTES) return null
            const trimmed = text.trimStart()
            // Decide as soon as there are enough characters to rule the prefix in or out.
            if (trimmed.length >= 20 && !ERROR_PREFIX.test(trimmed)) return null
        }
    } finally {
        reader.cancel().catch(() => {})
    }
}

// The body is re-wrapped already decoded; these would describe the wire format, not the new Response.
function decodedHeaders(response) {
    const headers = new Headers(response.headers)
    headers.delete("content-encoding")
    headers.delete("content-length")
    return headers
}

// Header values and statusText must be printable ASCII; Emma messages may not be.
function headerSafe(value) {
    return String(value).replace(/[^\x20-\x7E]/g, "?").slice(0, 200)
}

// The first request for each path after a bust skips Emma's cached copy.
function withFreshAfterBust(path) {
    const bustAt = lastBustAt()
    if (!bustAt || Date.now() - bustAt > FRESH_AFTER_BUST_MS || /[?&]fresh=/.test(path)) return path
    if (state.freshBustAt !== bustAt) {
        state.freshBustAt = bustAt
        state.freshPaths.clear()
    }
    if (state.freshPaths.has(path)) return path
    state.freshPaths.add(path)
    return path + (path.includes("?") ? "&" : "?") + "fresh=true"
}

// 401/403 (token), 408/429 (load) and 5xx are about Emma, not the record, so old data beats none.
// A 404 or 422 is about the record and must reach the caller.
function isFallbackStatus(status) {
    return status === 401 || status === 403 || status === 408 || status === 429 || status >= 500
}

// Keeps the body of a successful response, unless it is larger than LAST_GOOD_MAX_BYTES; reading
// stops at that size, so big feeds cost nothing here.
function remember(path, clone) {
    const reader = clone.body?.getReader()
    if (!reader) return
    const contentType = clone.headers.get("content-type")
    const decoder = new TextDecoder()
    let body = ""
    const read = async () => {
        for (;;) {
            const { done, value } = await reader.read()
            if (done) break
            body += decoder.decode(value, { stream: true })
            if (body.length > LAST_GOOD_MAX_BYTES) return reader.cancel()
        }
        body += decoder.decode()
        state.lastGood.delete(path) // re-insert so Map order tracks recency
        state.lastGood.set(path, { body, contentType, at: Date.now() })
        if (state.lastGood.size > LAST_GOOD_MAX_ENTRIES) state.lastGood.delete(state.lastGood.keys().next().value)
    }
    read().catch(() => {})
}

function staleResponse(path) {
    const entry = state.lastGood.get(path)
    if (!entry) return null
    const age = Math.round((Date.now() - entry.at) / 1000)
    logThrottled(`stale:${path}`, "warn", `[emma] serving last good response for ${path} (age ${age}s)`)
    return new Response(entry.body, {
        status: 200,
        headers: { "content-type": entry.contentType || "application/json", "x-emma-stale": String(age) },
    })
}

function reportFailure(path, status, message) {
    if (status === 404 || status === 422) return // ordinary "no such record"; callers handle it
    if (status === 401 || status === 403) {
        const hint = process.env.EMMA_API_TOKEN
            ? "EMMA_API_TOKEN was rejected; check it is current and valid for " + process.env.EMMA_API_URL
            : "EMMA_API_TOKEN is not set"
        logThrottled("auth", "error", `[emma] AUTH FAILURE ${status} (${message || "unauthorized"}): ${hint}. First seen on ${path}`)
        return
    }
    logThrottled(`fail:${status ?? "network"}`, "error", `[emma] request failed (${status ?? "network"}${message ? ": " + message : ""}) on ${path}`)
}

// At most one line per key per interval, so an outage doesn't flood the logs on every request.
function logThrottled(key, level, message) {
    const now = Date.now()
    if (now - (state.lastLogged.get(key) || 0) < LOG_INTERVAL_MS) return
    state.lastLogged.set(key, now)
    console[level](message)
}
